import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Megaphone, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const AUDIENCES = ["all", "sales", "dispatcher", "admin"];
const empty = { title: "", body: "", audience: "all", priority: "normal" };

export default function Announcements() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const invalidate = useInvalidate();
  const { data: anns = [], isLoading } = useList("announcements", "/announcements", { refetchInterval: 20000 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title || !form.body) { toast.error("Title and message required"); return; }
    setSaving(true);
    try { await api.post("/announcements", form); toast.success("Announcement posted"); setOpen(false); setForm(empty); invalidate("announcements"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete announcement?")) return; await api.delete(`/announcements/${id}`); invalidate("announcements"); };

  return (
    <div>
      <PageHeader title="Announcements" subtitle={isAdmin ? "Broadcast updates to your team" : "Updates from your admin"}
        actions={isAdmin && <Button data-testid="new-announcement-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />New Announcement</Button>} />

      {isLoading ? <div className="text-slate-500">Loading…</div> : anns.length === 0 ? (
        <Panel><EmptyState icon={Megaphone} title="No announcements" hint="Important updates will appear here." /></Panel>
      ) : (
        <div className="space-y-3">
          {anns.map((a) => (
            <Panel key={a.id} className="p-5 animate-fade-up" data-testid={`announcement-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-md ${a.priority === "urgent" ? "bg-red-500/15 text-red-400" : "bg-blue-100 text-blue-700"}`}><Megaphone size={20} weight="duotone" /></div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-slate-900">{a.title}</h3>
                      {a.priority === "urgent" && <StatusBadge status="high">urgent</StatusBadge>}
                      <span className="text-xs text-slate-500 capitalize">→ {a.audience}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.body}</p>
                    <p className="text-xs text-slate-500 mt-2">{a.author_name} · {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {isAdmin && <button onClick={() => remove(a.id)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash size={16} /></button>}
              </div>
            </Panel>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Title *"><Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-300" /></Field>
            <Field label="Message *"><Textarea data-testid="announcement-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-white border-slate-300" rows={4} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Audience">
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="announcement-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a} value={a} className="capitalize">{a === "all" ? "Everyone" : a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-announcement-btn" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Posting…" : "Post"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
